import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    VrTransactionView,
    VrStatusUpdate,
    VrScheduleAppointment,
} from "../../Redux/Actions/resellerActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import "./viewVRHistory.css";

const vrStatusOptions = [
    { value: "requested", label: "Requested" },
    { value: "declined", label: "Declined" },
    { value: "accepted", label: "Accepted" },
    { value: "scheduled", label: "Scheduled" },
    { value: "ongoing", label: "Ongoing" },
    { value: "completed", label: "Completed" },
    { value: "published", label: "Published" },
];

const ViewVrTransaction = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const { loading } = useSelector((state) => state.reseller || {});

    const [transactionData, setTransactionData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [vrStatusValue, setVrStatusValue] = useState("");
    const [vrStatusUpdating, setVrStatusUpdating] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState("");
    const [appointmentReason, setAppointmentReason] = useState("");
    const [appointmentUpdating, setAppointmentUpdating] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);

    const fetchDetails = async () => {
        try {
            setPageLoading(true);
            const res = await dispatch(VrTransactionView(id));
            if (res && res.statusCode === 200) {
                setTransactionData(res.message);
                setVrStatusValue(res.message?.vr_status || "");
                if (res.message?.vr_appointment?.date) {
                    setAppointmentDate(
                        new Date(res.message.vr_appointment.date).toISOString().slice(0, 16)
                    );
                }
                setAppointmentReason(res.message?.vr_appointment?.reason || "");
            } else {
                toast.error(res?.data || "Failed to load transaction details");
            }
        } catch (error) {
            toast.error("Error fetching transaction details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [dispatch, id]);

    const handleVrStatusUpdate = async () => {
        if (!vrStatusValue) return toast.error("Please select a VR status");
        if (vrStatusValue === "scheduled") {
            setShowAppointmentModal(true);
            return;
        }
        try {
            setVrStatusUpdating(true);
            const res = await dispatch(VrStatusUpdate(id, { vr_status: vrStatusValue }));
            if (res?.statusCode === 200) {
                setTransactionData((prev) => ({ ...prev, vr_status: vrStatusValue }));
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setVrStatusUpdating(false);
        }
    };

    const handleScheduleAppointment = async () => {
        if (!appointmentDate) return toast.error("Please select appointment date");
        try {
            setAppointmentUpdating(true);
            const payload = {
                date: new Date(appointmentDate).toISOString(),
                ...(appointmentReason && { reason: appointmentReason }),
            };
            const res = await dispatch(VrScheduleAppointment(id, payload));
            if (res?.statusCode === 200) {
                setTransactionData((prev) => ({
                    ...prev,
                    vr_appointment: { date: appointmentDate, reason: appointmentReason },
                    vr_status: "scheduled",
                }));
                setShowAppointmentModal(false);
            } else {
                toast.error(res?.data || "Failed to schedule appointment");
            }
        } catch (error) {
            toast.error("Something went wrong while scheduling appointment");
        } finally {
            setAppointmentUpdating(false);
        }
    };

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vvt-loader-wrap">
                    <div className="vvt-loader" />
                    <p className="vvt-loader-text">Loading transaction data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!transactionData) {
        return (
            <MainLayout>
                <div className="vvt-empty-wrap">
                    <div className="vvt-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                    </div>
                    <p className="vvt-empty-text">No Transaction Data Found.</p>
                    <Link to="/reseller" className="vvt-back-link">← Back to Transactions</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vvt-wrapper">

                {/* Header */}
                <div className="vvt-header">
                    <div className="vvt-header-left">
                        <Link to="/reseller" className="vvt-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vvt-header-divider" />
                        <div>
                            <div className="vvt-breadcrumb">VR Transaction / List / View</div>
                            <h1 className="vvt-title">{transactionData?.property?.property_name || "Transaction Detail"}</h1>
                        </div>
                    </div>
                </div>

                {/* Hero Card */}
                <div className="vvt-hero-card">
                    <div className="vvt-hero-left">
                        <div className="vvt-hero-avatar">
                            {transactionData?.staff?.name?.charAt(0)?.toUpperCase() || "R"}
                        </div>
                        <div>
                            <span className="vvt-hero-label">Reseller Name</span>
                            <span className="vvt-hero-name">{transactionData?.staff?.name || "—"}</span>
                            {transactionData?.staff?.email && (
                                <span className="vvt-hero-brand">{transactionData.staff.email}</span>
                            )}
                        </div>
                    </div>

                    <div className="vvt-hero-meta">
                        {transactionData?.payment_status && (
                            <div className="vvt-hero-meta-item">
                                <span className="vvt-hero-label">Payment Status</span>
                                <div className={`vvt-status-chip vvt-status-chip--${transactionData.payment_status}`}>
                                    {transactionData.payment_status.replace(/_/g, " ")}
                                </div>
                            </div>
                        )}
                        {transactionData?.vr_status && (
                            <div className="vvt-hero-meta-item">
                                <span className="vvt-hero-label">VR Status</span>
                                <div className={`vvt-status-chip vvt-status-chip--${transactionData.vr_status}`}>
                                    {transactionData.vr_status}
                                </div>
                            </div>
                        )}
                        {transactionData?.amount && (
                            <div className="vvt-hero-meta-item">
                                <span className="vvt-hero-label">Amount</span>
                                <span className="vvt-hero-amount">
                                    ₹{transactionData.amount?.toLocaleString("en-IN")}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="vvt-grid-layout">
                    {/* Left Column */}
                    <div className="vvt-col">

                        {/* Payment Info */}
                        <div className="vvt-card">
                            <div className="vvt-card-header">
                                <span className="vvt-card-badge">01</span>
                                <h2 className="vvt-card-title">Payment Information</h2>
                            </div>
                            <div className="vvt-info-grid">
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Booking Amount</span>
                                    <span className="vvt-info-value">₹{transactionData?.amount?.toLocaleString("en-IN") || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Currency</span>
                                    <span className="vvt-info-value">{transactionData.payment_plan.order.entity.currency || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Payment Status</span>
                                    <span className="vvt-info-value">{transactionData?.payment_status?.replace(/_/g, " ") || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Refund</span>
                                    <span className="vvt-info-value">{transactionData?.is_refund ? "Yes" : "No"}</span>
                                </div>
                                <div className="vvt-info-item vvt-info-item--full">
                                    <span className="vvt-info-label">Razorpay Order ID</span>
                                    <span className="vvt-info-value">{transactionData?.payment_plan?.order?.entity?.id || "—"}</span>
                                </div>
                                <div className="vvt-info-item vvt-info-item--full">
                                    <span className="vvt-info-label">Razorpay Payment ID</span>
                                    <span className="vvt-info-value">{transactionData?.payment_plan?.payment?.entity?.id || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Payment Method</span>
                                    <span className="vvt-info-value">{transactionData?.payment_plan?.payment?.entity?.method?.toUpperCase() || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">UPI / VPA</span>
                                    <span className="vvt-info-value">
                                        {transactionData?.payment_plan?.payment?.entity?.vpa ||
                                            transactionData?.payment_plan?.payment?.entity?.upi?.vpa || "—"}
                                    </span>
                                </div>
                                <div className="vvt-info-item vvt-info-item--full">
                                    <span className="vvt-info-label">Transaction ID</span>
                                    <span className="vvt-info-value">{transactionData?.transection_id || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Payment Date</span>
                                    <span className="vvt-info-value">
                                        {transactionData?.created_at
                                            ? new Date(transactionData.created_at).toLocaleString("en-IN")
                                            : "—"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Update VR Status */}
                        <div className="vvt-card">
                            <div className="vvt-card-header">
                                <span className="vvt-card-badge">02</span>
                                <h2 className="vvt-card-title">
                                    {vrStatusValue === "scheduled" ? "Update VR Status & Schedule Appointment" : "Update VR Status"}
                                </h2>
                            </div>
                            <div className="vvt-status-panel">
                                <div className="vvt-status-field">
                                    <label className="vvt-info-label">Select VR Status</label>
                                    <select
                                        className="vvt-status-select"
                                        value={vrStatusValue}
                                        onChange={(e) => setVrStatusValue(e.target.value)}
                                        disabled={vrStatusUpdating || loading}
                                    >
                                        <option value="">Select status</option>
                                        {vrStatusOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    type="button"
                                    className="vvt-status-btn"
                                    onClick={handleVrStatusUpdate}
                                    disabled={vrStatusUpdating || loading || !vrStatusValue}
                                >
                                    {vrStatusUpdating || loading
                                        ? "Updating..."
                                        : vrStatusValue === "scheduled"
                                            ? "Update & Schedule Appointment"
                                            : "Update VR Status"}
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* Right Column */}
                    <div className="vvt-col">

                        {/* Property Info */}
                        <div className="vvt-card">
                            <div className="vvt-card-header">
                                <span className="vvt-card-badge">03</span>
                                <h2 className="vvt-card-title">Property Information</h2>
                            </div>
                            <div className="vvt-info-grid">
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Property Name</span>
                                    <span className="vvt-info-value">{transactionData?.property?.property_name || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Property ID</span>
                                    <span className="vvt-info-value">{transactionData?.property?.property_id || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Business Type</span>
                                    <span className="vvt-info-value">{transactionData?.property?.business_type || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Status</span>
                                    <span className="vvt-info-value">{transactionData?.property?.status || "—"}</span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Starting Price</span>
                                    <span className="vvt-info-value">
                                        {transactionData?.property?.starting_price
                                            ? `₹${transactionData.property.starting_price.toLocaleString("en-IN")}`
                                            : "—"}
                                    </span>
                                </div>
                                <div className="vvt-info-item">
                                    <span className="vvt-info-label">Construction Status</span>
                                    <span className="vvt-info-value">{transactionData?.property?.construction_status || "—"}</span>
                                </div>
                                <div className="vvt-info-item vvt-info-item--full">
                                    <span className="vvt-info-label">Location</span>
                                    <span className="vvt-info-value">
                                        {[
                                            transactionData?.property?.city?.city_name,
                                            transactionData?.property?.state?.state_name,
                                            transactionData?.property?.country?.country_name,
                                        ].filter(Boolean).join(", ") || "—"}
                                    </span>
                                </div>
                                <div className="vvt-info-item vvt-info-item--full">
                                    <span className="vvt-info-label">Street Address</span>
                                    <span className="vvt-info-value">{transactionData?.property?.street_address || "—"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Appointment Info */}
                        {transactionData?.vr_status === "scheduled" && transactionData?.vr_appointment?.date && (
                            <div className="vvt-card">
                                <div className="vvt-card-header">
                                    <span className="vvt-card-badge">04</span>
                                    <h2 className="vvt-card-title">VR Appointment</h2>
                                </div>
                                <div className="vvt-info-grid">
                                    <div className="vvt-info-item">
                                        <span className="vvt-info-label">Appointment Date</span>
                                        <span className="vvt-info-value">
                                            {new Date(transactionData.vr_appointment.date).toLocaleString("en-IN")}
                                        </span>
                                    </div>
                                    <div className="vvt-info-item">
                                        <span className="vvt-info-label">Reschedule Reason</span>
                                        <span className="vvt-info-value">{transactionData?.reschedule_reason || "—"}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* Appointment Modal */}
            {showAppointmentModal && (
                <div className="vvt-modal-overlay">
                    <div className="vvt-modal">
                        <div className="vvt-modal-header">
                            <h2 className="vvt-modal-title">Schedule VR Appointment</h2>
                            <button
                                className="vvt-modal-close"
                                onClick={() => setShowAppointmentModal(false)}
                            >✕</button>
                        </div>
                        <div className="vvt-status-panel">
                            <div className="vvt-status-field">
                                <label className="vvt-info-label">Appointment Date & Time</label>
                                <input
                                    type="datetime-local"
                                    className="vvt-status-select"
                                    value={appointmentDate}
                                    onChange={(e) => setAppointmentDate(e.target.value)}
                                    disabled={appointmentUpdating}
                                />
                            </div>
                            <div className="vvt-modal-actions">
                                <button
                                    type="button"
                                    className="vvt-status-btn"
                                    onClick={handleScheduleAppointment}
                                    disabled={appointmentUpdating || !appointmentDate}
                                    style={{ flex: 1 }}
                                >
                                    {appointmentUpdating ? "Scheduling..." : "Confirm Appointment"}
                                </button>
                                <button
                                    type="button"
                                    className="vvt-modal-cancel"
                                    onClick={() => setShowAppointmentModal(false)}
                                    disabled={appointmentUpdating}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </MainLayout>
    );
};

export default ViewVrTransaction;